import { useTranslation } from "react-i18next";
import type { Celebrity } from "../../lib/types";
import { SeatSlot } from "./SeatSlot";

interface TableViewProps {
  seats: (Celebrity | null)[];
  readonly: boolean;
  displayMode?: boolean;
  onAdd?: (index: number) => void;
  onRemove?: (index: number) => void;
}

export function TableView({
  seats,
  readonly,
  displayMode,
  onAdd,
  onRemove,
}: TableViewProps) {
  const { t } = useTranslation();
  const topSeats = [0, 1, 2];
  // Reversed bottom row so seats 5,4,3 face the top row guests
  const bottomSeats = [5, 4, 3];

  if (displayMode) {
    return (
      <div className="relative flex flex-col items-center rounded-3xl overflow-hidden bg-stone-900/5 dark:bg-black/30 px-3 sm:px-6 py-4 sm:py-8 gap-0">
        {/* Ambient candlelight glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(251,191,36,0.06) 0%, transparent 70%)",
          }}
        />

        {/* Top row — leaning in */}
        <div className="flex gap-2 sm:gap-4 relative z-10">
          {topSeats.map((i) => (
            <SeatSlot
              key={i}
              celebrity={seats[i] ?? null}
              readonly={readonly}
              displayMode={displayMode}
            />
          ))}
        </div>

        {/* Table surface */}
        <div className="relative w-full z-10 my-3">
          <div className="h-12 sm:h-16 rounded-[2rem] bg-gradient-to-b from-amber-950/25 to-stone-900/30 dark:from-amber-950/50 dark:to-stone-950/60 border border-amber-900/20 dark:border-amber-800/30 shadow-inner flex items-center justify-center gap-4 sm:gap-6">
            <span className="text-xl select-none" aria-hidden>
              🕯️
            </span>
            <span className="text-amber-900/40 dark:text-amber-600/40 text-[10px] tracking-[0.3em] uppercase font-medium">
              {t("table.label")}
            </span>
            <span className="text-xl select-none" aria-hidden>
              🕯️
            </span>
          </div>
        </div>

        {/* Bottom row — leaning in from the other side */}
        <div className="flex gap-2 sm:gap-4 relative z-10">
          {bottomSeats.map((i) => (
            <SeatSlot
              key={i}
              celebrity={seats[i] ?? null}
              readonly={readonly}
              displayMode={displayMode}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-2">
      {/* Top row */}
      <div className="flex gap-2 sm:gap-4">
        {topSeats.map((i) => (
          <SeatSlot
            key={i}
            celebrity={seats[i] ?? null}
            readonly={readonly}
            onAdd={onAdd ? () => onAdd(i) : undefined}
            onRemove={onRemove ? () => onRemove(i) : undefined}
          />
        ))}
      </div>

      {/* Table surface divider */}
      <div className="bg-stone-200/60 dark:bg-stone-800/60 rounded-2xl h-8 w-full my-2 border border-stone-300/40 dark:border-stone-700/40 flex items-center justify-center">
        <span className="text-stone-400 dark:text-stone-700 text-xs tracking-widest uppercase">
          {t("table.label")}
        </span>
      </div>

      {/* Bottom row (reversed visually) */}
      <div className="flex gap-2 sm:gap-4">
        {bottomSeats.map((i) => (
          <SeatSlot
            key={i}
            celebrity={seats[i] ?? null}
            readonly={readonly}
            onAdd={onAdd ? () => onAdd(i) : undefined}
            onRemove={onRemove ? () => onRemove(i) : undefined}
          />
        ))}
      </div>
    </div>
  );
}
